import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import type { Query } from '../types/definitionsType';

const STORAGE_KEY = 'teamSchedule_filterState';
const logger = useLogger('TeamSchedule -> filterSessionStorage');

/**
 * Structured filter state persisted to sessionStorage.
 * Replaces the previous approach of serializing all filter data into URL query params,
 * which caused HTTP 414 errors when many users/teams were selected.
 *
 * sessionStorage is scoped per tab, survives page reloads, and has ~5MB storage
 * (vs ~2KB typical URL length limit).
 */
export interface FilterState {
    facets?: Query['facets'];
    timezone?: string;
    selectedView?: string;
    colorMode?: string;
    groupBy?: string;
    columnDisplay?: { userId: boolean; email: boolean };
    searchDateFilter?: string;
    dateRange?: string;
    searchType?: string;
    facetVisibility?: Record<string, string[]>;
    /** ISO timestamp of last update — useful for debugging & staleness checks */
    _updatedAt?: string;
}

/**
 * Save the complete filter state to sessionStorage.
 * Silently handles quota/security errors so the app never crashes from storage failures.
 */
export function saveFilterState(state: FilterState): void {
    try {
        const payload: FilterState = {
            ...state,
            _updatedAt: new Date().toISOString()
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
        logger.error('Failed to save filter state to sessionStorage', e);
    }
}

/**
 * Read the complete filter state from sessionStorage.
 * Returns `null` if nothing is stored, data is corrupted, or sessionStorage is unavailable.
 */
export function loadFilterState(): FilterState | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as FilterState;
        // Basic shape validation — must be a non-null object
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            logger.error('Filter state in sessionStorage has unexpected shape, discarding');
            clearFilterState();
            return null;
        }
        return parsed;
    } catch (e) {
        logger.error('Failed to load filter state from sessionStorage', e);
        clearFilterState();
        return null;
    }
}

/**
 * Remove filter state from sessionStorage (e.g. when user clears all filters).
 */
export function clearFilterState(): void {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        logger.error('Failed to clear filter state from sessionStorage', e);
    }
}

/**
 * Merge partial updates into the existing filter state.
 * This is the primary write API — callers supply only the keys they want to update.
 */
export function updateFilterState(partial: Partial<FilterState>): void {
    const current = loadFilterState() || {};
    saveFilterState({ ...current, ...partial });
}

/**
 * Read facets from sessionStorage (replaces `parseFacetsFromUrl` for facet components).
 * Returns the facets object or null.
 */
export function loadFacetsFromSession(): Query['facets'] | null {
    const state = loadFilterState();
    return state?.facets ?? null;
}
